import _ from "lodash";
import { useEffect, useState } from "react";
import { IDeleteFilesCmd, IMoveFilesCmd, IUpdateMultipleFilesCmd, IUploadFile, OwnerType } from "@/twapp/server/FileEndpoint";
import { TromanSystem, useApiServer, useDialog, useDownload, useSystemInfo, useTromanSystem, useUserServer } from "../../hooks/useTromanService";
import { IContainer, IFile, IFileRoot } from "./model/fileModel";
import { IPreparedUpload, prepareFileUpload } from "./FileUploadDialog/fileUpload";
import { parseFileRoot } from "./model/fileParse";
import { FileManager } from "./FileManager";
import { FileUploadDialog } from "./FileUploadDialog/FileUploadDialog";
import { FileUploadProgress } from "./FileUploadProgress/FileUploadProgress";
import { IPreparedMove, prepareFileMove } from "./FileMoveDialog/fileMove";
import { FileMoveDialog } from "./FileMoveDialog/FileMoveDialog";
import { FileDeleteDialog, IPreparedDelete, prepareDelete } from "./FileDeleteDialog/FileDeleteDialog";
import { FileEditDialog, IFileEditResult } from "./FileEditDialog/FileEditDialog";
import { FileMultiUpdateDialog, IPreparedMultiUpdate, prepareMultiUpdate } from "./FileMultiUpdateDialog/FileMultiUpdateDialog";
import { IApiPromise } from "@/twapp/server/ApiService";
import { ErrorDisplay } from "../ErrorDisplay";
import { ImMap } from "../../tromanImmutable";
import { UserData } from "@/common/account/UserData";
import { IPreparedEdit, prepareCreateFolder, prepareEdit } from "./FileEditDialog/fileEdit";

export interface FileManagerComponentProps {
    ownerId: string;
    ownerType: OwnerType;
}

export function FileManagerComponent({ownerId, ownerType}: FileManagerComponentProps) {
    const tromanSystem = useTromanSystem();
    const api = useApiServer();
    const usersApi = useUserServer();
    const dialogService = useDialog();
    const systemInfo = useSystemInfo();
    const downloadService = useDownload();

    const [users, setUsers] = useState<ImMap<string, UserData>>();
    const [error, setError] = useState<any>();
    const [root, setRoot] = useState<IFileRoot>();
    const [prepared, setPrepared] = useState<IPreparedUpload>();
    const [uploading, setUploading] = useState<IUploadFile[]>();
    const [moving, setMoving] = useState<IPreparedMove>();
    const [deleting, setDeleting] = useState<IPreparedDelete>();
    const [editing, setEditing] = useState<IPreparedEdit>();
    const [multiUpdate, setMultiUpdate] = useState<IPreparedMultiUpdate>();
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        setBusy(true);
        if (ownerId) {
            Promise.all([
                api.Files.listFiles({ OwnerId: ownerId, OwnerType: ownerType }),
                usersApi.getAllUsers(),
            ])
            .then(data => {
                setUsers(ImMap(data[1].map(x => [x.Id, x])));
                setRoot(parseFileRoot(ownerId, ownerType, data[0]));
            })
            .catch(setError)
            .finally(() => setBusy(false));
        } else {
            setRoot(undefined);
        }
    }, [ownerId, ownerType]);
    
    if (error) {
        return (
            <ErrorDisplay error={error} title="Fel vid hämtning av dokument"/>
        )
    }

    if (!root || !users) {
        return (
            <div>Hämtar dokument...</div>
        );
    }

    const loadRoot = () => api.Files.listFiles({ OwnerId: ownerId, OwnerType: ownerType })
        .then(res => setRoot(parseFileRoot(ownerId, ownerType, res)))
        .catch(setError);

    const prepareFiles = (target: IContainer, files: FileList) => {
        const preparedFiles = prepareFileUpload(root, target, files, systemInfo.AttachmentExtensions);

        if (preparedFiles.hasErrors) {
            setPrepared(preparedFiles);
        } else {
            setBusy(true);
            setUploading(preparedFiles.valid);
        }
    };

    const uploadFiles = (files?: IUploadFile[]) => {
        setPrepared(undefined);
        if (files) {
            setBusy(true);
            setUploading(files);
        }
    };

    const uploadComplete = (changed: boolean) => {
        loadRoot().finally(() => {
            setBusy(false);
        });
    };

    const uploadClose = () => {
        setUploading(undefined);
    };

    const downloadFile = (file: IFile) => {
        api.Files.downloadAttachment(file.id)
            .then(x => downloadService.downloadBlob(x.blob, x.filename))
            .catch(dialogService.error);
    };

    function performCmd<C>(cmd: C|undefined, cb: (cmd: C) => IApiPromise<any>, setUndefined: (x: undefined) => void) {
        if (cmd) {
            setBusy(true);
            cb(cmd).then(loadRoot).finally(() => { setBusy(false); setUndefined(undefined); });
        } else {
            setUndefined(undefined);
        }
    }

    const deleteFiles = (cmd?: IDeleteFilesCmd) => performCmd(cmd, x => api.Files.deleteFiles(x), setDeleting);
    const moveFile = (cmd?: IMoveFilesCmd) => performCmd(cmd, x => api.Files.moveFiles(x), setMoving);
    const updateMultiple = (cmd?: IUpdateMultipleFilesCmd) => performCmd(cmd, x => api.Files.updateMultipleFiles(x), setMultiUpdate);
    const updateFile = (result?: IFileEditResult) => {
        if (result?.update) {
            performCmd(result.update, x => api.Files.updateSingleFile(x), setEditing);
        } else {
            performCmd(result?.createFolder, x => api.Files.createFolder(x), setEditing);
        }
    };

    const hasPublish = ownerType === OwnerType.Meeting && tromanSystem === TromanSystem.Troman;

    return (
        <div>
            <FileManager root={root}
                hasPublish={hasPublish}
                uploadFiles={prepareFiles}
                createFolder={c => setEditing(prepareCreateFolder(c))}
                editEntry={(c,e) => setEditing(prepareEdit(c, e, users))}
                moveFiles={x => setMoving(prepareFileMove(root, x))}
                deleteFiles={x => setDeleting(prepareDelete(x))}
                updateMultiple={x => setMultiUpdate(prepareMultiUpdate(x))}
                downloadFile={downloadFile}/>
            <FileUploadDialog files={prepared} onClose={uploadFiles}/>
            <FileUploadProgress files={uploading} busy={busy} onComplete={uploadComplete} onClose={uploadClose}/>
            <FileMoveDialog files={moving} busy={busy} root={root} onClose={moveFile}/>
            <FileDeleteDialog files={deleting} busy={busy} root={root} onClose={deleteFiles}/>
            <FileEditDialog editing={editing} busy={busy} root={root} hasPublish={hasPublish} onClose={updateFile}/>
            <FileMultiUpdateDialog files={multiUpdate} busy={busy} root={root} onClose={updateMultiple}/>
        </div>
    )
}
